import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import JourneyStepper from "@/components/JourneyStepper";
import { stageChipClass } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { ArrowRight } from "lucide-react";

import ProfileTab from "./client-workspace/ProfileTab";
import FamilyTab from "./client-workspace/FamilyTab";
import AssetsTab from "./client-workspace/AssetsTab";
import InsuranceTab from "./client-workspace/InsuranceTab";
import GoalsTab from "./client-workspace/GoalsTab";
import TimelineTab from "./client-workspace/TimelineTab";
import RiskAssessmentTab from "./client-workspace/RiskAssessmentTab";
import DiscoveryCallTab from "./client-workspace/DiscoveryCallTab";
import PlanTab from "./client-workspace/PlanTab";
import ExecutionTab from "./client-workspace/ExecutionTab";
import ReviewsTab from "./client-workspace/ReviewsTab";
import DocumentsTab from "./client-workspace/DocumentsTab";
import ProjectsTab from "./client-workspace/ProjectsTab";
import CommTab from "./client-workspace/CommTab";
import HeldFundsTab from "./client-workspace/HeldFundsTab";
import ToolsPage from "./ToolsPage";
import InsuranceRenewalBanner from "@/components/InsuranceRenewalBanner";
import { AdminClientActions } from "@/components/AdminClientActions";

export default function ClientWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  // Deep-link support: /app/clients/:id?tab=plan → open Written Plan tab
  const initialTab = (typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("tab") : null) || "profile";
  // Tabs is now controlled so other UI (e.g. the renewal banner's "Open
  // Insurance" button) can switch tabs programmatically.
  const [activeTab, setActiveTab] = useState(initialTab);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${id}`);
      setClient(data);
    } catch {
      toast.error("Not accessible");
      navigate("/app/clients");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async (updates) => {
    try {
      await api.patch(`/clients/${id}`, updates);
      toast.success("Saved");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const advance = async () => {
    try {
      await api.post(`/clients/${id}/advance-stage`);
      toast.success("Journey advanced");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Cannot advance"); }
  };

  if (loading || !client) return <div className="p-10 text-center text-muted-foreground">Loading client…</div>;

  const canEdit = user.role === "Admin" || user.role === "Advisor";
  const isServiceRep = user.role === "ServiceRep";
  const isExecution = user.role === "Execution";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mb-2 flex items-center gap-3">
            <button onClick={() => navigate("/app/clients")} className="text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-clients">← Clients</button>
            <span className={stageChipClass(client.journey_stage)} data-testid="client-journey-stage">{client.journey_stage}</span>
          </div>
          <h1 className="font-dashboard text-3xl font-bold tracking-tight" data-testid="client-name">{client.full_name}</h1>
          <p className="text-sm text-muted-foreground">{client.email} · {client.mobile}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {user.role === "Admin" && <AdminClientActions client={client} onChanged={load} />}
          {canEdit && client.journey_stage !== "Ongoing Review" && (
            <Button variant="outline" onClick={advance} className="gap-2" data-testid="advance-stage-btn">
              Advance stage <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <Card className="border-border bg-white p-5">
        <JourneyStepper current={client.journey_stage} />
      </Card>

      {!isServiceRep && (
        <InsuranceRenewalBanner
          clientId={client.id}
          onJumpToInsurance={() => setActiveTab("insurance")}
        />
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex w-full flex-wrap justify-start gap-1 bg-transparent p-0">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          {!isServiceRep && !isExecution && <TabsTrigger value="discovery" data-testid="tab-discovery">Discovery Call</TabsTrigger>}
          {!isServiceRep && <TabsTrigger value="family" data-testid="tab-family">Family</TabsTrigger>}
          {!isServiceRep && !isExecution && <TabsTrigger value="assets" data-testid="tab-assets">Assets & Liab.</TabsTrigger>}
          {!isServiceRep && !isExecution && <TabsTrigger value="held-funds" data-testid="tab-held-funds">Held Funds</TabsTrigger>}
          {!isServiceRep && <TabsTrigger value="insurance" data-testid="tab-insurance">Insurance</TabsTrigger>}
          {!isServiceRep && <TabsTrigger value="goals" data-testid="tab-goals">Goals</TabsTrigger>}
          {!isServiceRep && <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>}
          {!isServiceRep && !isExecution && <TabsTrigger value="risk" data-testid="tab-risk">Risk</TabsTrigger>}
          {!isServiceRep && <TabsTrigger value="plan" data-testid="tab-plan">Written Plan</TabsTrigger>}
          {!isServiceRep && !isExecution && <TabsTrigger value="tools" data-testid="tab-tools">Tools</TabsTrigger>}
          {!isServiceRep && <TabsTrigger value="execution" data-testid="tab-execution">Execution</TabsTrigger>}
          {!isServiceRep && !isExecution && <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>}
          {!isServiceRep && <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>}
          {!isServiceRep && <TabsTrigger value="projects" data-testid="tab-projects">Projects</TabsTrigger>}
          {!isServiceRep && <TabsTrigger value="comm" data-testid="tab-comm">Communication</TabsTrigger>}
        </TabsList>

        <div className="mt-6">
          <TabsContent value="profile"><ProfileTab client={client} onSave={save} canEdit={canEdit} user={user} /></TabsContent>
          {!isServiceRep && !isExecution && <TabsContent value="discovery"><DiscoveryCallTab clientId={client.id} /></TabsContent>}
          <TabsContent value="family"><FamilyTab client={client} onSave={save} canEdit={canEdit} /></TabsContent>
          <TabsContent value="assets"><AssetsTab client={client} onSave={save} canEdit={canEdit} /></TabsContent>
          <TabsContent value="held-funds"><HeldFundsTab clientId={client.id} canEdit={canEdit} /></TabsContent>
          <TabsContent value="insurance"><InsuranceTab client={client} onSave={save} canEdit={canEdit} /></TabsContent>
          <TabsContent value="goals"><GoalsTab client={client} onSave={save} canEdit={canEdit} /></TabsContent>
          <TabsContent value="timeline"><TimelineTab client={client} /></TabsContent>
          <TabsContent value="risk"><RiskAssessmentTab clientId={client.id} canEdit={canEdit} /></TabsContent>
          <TabsContent value="plan"><PlanTab client={client} canEdit={canEdit} /></TabsContent>
          <TabsContent value="tools"><ToolsPage clientId={client.id} /></TabsContent>
          <TabsContent value="execution"><ExecutionTab clientId={client.id} canEdit={!isServiceRep} reload={load} /></TabsContent>
          <TabsContent value="reviews"><ReviewsTab client={client} canEdit={canEdit} reload={load} /></TabsContent>
          <TabsContent value="documents"><DocumentsTab clientId={client.id} canEdit={canEdit} /></TabsContent>
          <TabsContent value="projects"><ProjectsTab clientId={client.id} canEdit={!isServiceRep} /></TabsContent>
          {!isServiceRep && <TabsContent value="comm"><CommTab clientId={client.id} /></TabsContent>}
        </div>
      </Tabs>
    </div>
  );
}
